(ns ycweb.app.website.cart.parts.common
  (:require
   [rum.core :as rum]
   [util.string :as str]
   [ycweb.app.website.cart.model :as model]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.util.core :as u]
   [ycweb.util.date :as date]
   [ycweb.util.urlstate :as urlstate]
   [ycweb.components.toaster :as toaster]))

(def step-def
  (->>
    [{:id :cart/items
      :title "Your cart"
      :step 1
      :back ["/" "Continue shopping"]
      :next :delivery/method
      :button-style :cta
      :button-text "Checkout"
      :properties #{:save-cart}}

     {:id :cart/save-for-later
      :title "Save cart and check out later"
      :step 1
      :back :cart/items
      :next :cart/saved
      :button-style :primary
      :button-text "Save my cart"

      :after-update
      (fn [app-atom process-atom cart-summary-atom click-fn]
        (model/save-cart-later app-atom (:save-later @process-atom)
          (fn [response]
            (when (:success response)
              (click-fn)
              (model/set-cart-uuid app-atom nil)
              (reset! cart-summary-atom {})))))}

     {:id :cart/saved
      :title "Success! Your cart has been saved"
      :step 1
      :back ["/" "Continue shopping"]
      :button-style :primary
      :button-text "Start shopping"
      :continue-fn (fn [app-atom] (urlstate/redirect! "/"))
      :ignore-empty-cart? true
      :properties #{:hide-amount}}

     {:id :delivery/method
      :title "Delivery options"
      :step 2
      :back :cart/items
      :next :details/address
      :on-navigate
      (fn [cart-summary]
        (cond 
          (model/has-registry-purchase-items? cart-summary)
          :details/giftmessage

          ;; Skip this page if there are no physical items in the cart (digital only)
          (not (model/has-physical-items? cart-summary))
          :details/billing))}

     {:id :details/address
      :title "Recipient delivery address"
      :step 3
      :back :delivery/method
      :next :details/instructions}

     {:id :details/instructions
      :title "Delivery instructions"
      :step 3
      :back :details/address
      :next :details/giftmessage
      :properties #{:skippable}
      :skip-to :details/giftmessage
      :on-navigate
      (fn [cart-summary]
        (when
          (or
            (not (model/has-physical-items? cart-summary))
            (model/has-registry-purchase-items? cart-summary))
          :details/giftmessage))}

     {:id :details/giftmessage
      :title "Gift message"
      :step 3
      :back :details/instructions
      :next :details/billing
      :properties #{:skippable}
      :skip-to :details/billing
      :on-navigate
      (fn [cart-summary]
        (when
          (not
            (or
              (model/has-physical-items? cart-summary)
              (model/has-registry-purchase-items? cart-summary)))
          :details/billing))}

     {:id :details/billing
      :title "Billing address"
      :step 3
      :back :details/giftmessage
      :next :details/newsletter}

     {:id :details/newsletter
      :title "Newsletter signup"
      :step 3
      :back :details/billing
      :next :payment/method
      :skip-to :payment/method
      :properties #{:skippable}
      :on-navigate
      (fn [cart-summary]
        (when (get-in cart-summary [:user :newsletter_subscribed])
          :payment/method))}

     {:id :payment/method
      :title "Payment"
      :step 4
      :button-text "Order & Pay"
      :back :details/billing
      :next :payment/capture
      :on-navigate
      (fn [cart-summary]
        ;; Skip this page if balance is 0 or less
        (when (<= (:balance (:totals cart-summary)) 0)
          :order/confirmation))}

     {:id :payment/method-postcheckout
      :title "Payment"
      :step 4
      :back ["/" "Continue shopping"]
      :properties #{:checked-out}
      :ignore-empty-cart? true
      :button-text "Order & Pay"
      :button-style :cta
      :next :payment/capture}

     {:id :payment/capture
      :title "Payment"
      :step 4
      :properties #{:checked-out}
      :ignore-empty-cart? true
      :back :payment/method-postcheckout
      :next :order/confirmation
      :terminus-substeps #{:success :error}}

     {:id :order/confirmation
      :title "Order Confirmation"
      :step 5
      :properties #{:checked-out}
      :ignore-empty-cart? true
      :back ["/" "Continue shopping"]
      :button-text "Start Shopping"
      :continue-fn (fn [app-atom] (urlstate/redirect! "/"))}]
    (map (juxt :id identity))
    (into {})))

;; Helper functions for step navigation using step-def
(defn current-step-info [process-atom]
  (let [current-step (cmn/curact process-atom [:step])]
    (get step-def current-step)))

;; Substep stack functions for managing sub-navigation within a step (e.g., payment flows)
(defn push-substep
  "Push a substep onto the substep stack. Used for tracking navigation within a major step."
  [process-atom substep]
  (swap! process-atom update :substep-stack (fnil conj []) substep))

(defn pop-substep
  "Pop the current substep from the stack and return the new current substep (or nil if empty)"
  [process-atom]
  (let [stack (get @process-atom :substep-stack [])
        popped-stack (pop stack)]
    (swap! process-atom assoc :substep-stack popped-stack)
    (peek popped-stack)))

(defn current-substep
  "Get the current substep from the top of the stack (or nil if empty)"
  [process-atom]
  (peek (or (cmn/curact process-atom [:substep-stack]) [])))

(defn clear-substeps
  "Clear the substep stack. Called when navigating to a new major step."
  [process-atom]
  (swap! process-atom assoc :substep-stack []))

(defn resolve-target-step
  "Starting from the provided step key, evaluate that step's :on-navigate
   function (if present) with the given cart summary. If it returns a non-nil
   step key that is different from the current candidate, follow it and repeat.
   Stop when the :on-navigate returns nil or the same step, or if a cycle/depth
   limit is detected. Returns the resolved, terminal step key."
  [start-step-key cart-summary]
  (loop [candidate-key start-step-key
         visited-steps #{}
         depth 0]
    (let [candidate-step (get step-def candidate-key)
          on-navigate-fn (:on-navigate candidate-step)
          next-key (when on-navigate-fn (on-navigate-fn cart-summary))]
      (cond
        (nil? candidate-step)
        start-step-key

        (or (nil? next-key)
          (= next-key candidate-key))
        candidate-key

        (visited-steps next-key)
        candidate-key

        (>= depth 16)
        candidate-key

        :else
        (recur next-key (conj visited-steps candidate-key) (inc depth))))))

(defn resolve-next-from-step
  "Given a current step key, compute the effective next step after applying
   the next step's on-navigate chain. Returns a step keyword or nil."
  [current-step-key cart-summary]
  (let [current-step (get step-def current-step-key)
        raw-next (:next current-step)]
    (when raw-next
      (resolve-target-step raw-next cart-summary))))

(defn build-canonical-path
  "Construct the canonical step path from the start (:cart/items) by applying
   resolve-next-from-step repeatedly under the current cart state. Stops when
   no further next step exists or upon detecting a loop/depth limit."
  [cart-summary]
  (loop [path [:cart/items]
         visited-steps #{}
         depth 0]
    (let [current (peek path)
          next-key (resolve-next-from-step current cart-summary)]
      (cond
        (nil? next-key) path
        (visited-steps next-key) path
        (>= depth 32) path
        :else (recur (conj path next-key)
                (conj visited-steps current)
                (inc depth))))))

(defn next-step-key [process-atom cart-summary-atom]
  (let [step-info (current-step-info process-atom)
        next-key (:next step-info)
        cart-summary (cmn/curval cart-summary-atom)
        target-step
        (when next-key
          (resolve-target-step next-key cart-summary))]
    target-step))

(defn back-step-key
  "Navigate to the previous logical step by reconstructing the canonical path
   from :cart/items using the current cart summary, then moving to the
   predecessor of the current step. Falls back to the static :back key if the
   current step is not on the canonical path."
  [process-atom cart-summary-atom]
  (let [current-step (cmn/curact process-atom [:step])
        cart-summary @cart-summary-atom
        path (build-canonical-path cart-summary)
        idx (or (some (fn [[i step]] (when (= step current-step) i))
                  (map-indexed vector path))
              -1)]
    (cond
      (pos? idx) (nth path (dec idx))
      (= idx 0) nil
      :else
      (let [step-info (get step-def current-step)
            back-info (:back step-info)]
        (when (keyword? back-info)
          back-info)))))

(defn skip-step-key [process-atom cart-summary-atom]
  (let [step-info (current-step-info process-atom)
        skip-key (:skip-to step-info)
        cart-summary (cmn/curval cart-summary-atom)
        target-step
        (when skip-key
          (resolve-target-step skip-key cart-summary))]
    target-step))

(defn navigate-click-fn [app-atom process-atom cart-atom direction]
  (let [key-fn
        (case direction
          :next next-step-key
          :back back-step-key
          :skip skip-step-key)
        k (key-fn process-atom cart-atom)

        href-props
        (when k
          (urlstate/href-props app-atom "order" "ShowCart"
            {:step
             (u/keyword-string (key-fn process-atom cart-atom))}))]

    (when (:on-click href-props)
      (:on-click href-props))))

(def paygate-methods-base #{"Card" "Amex" "Mobicred" "Payflex"})
(def paygate-ewallet-methods #{"Zapper" "SnapScan"})

(defn- try-paygate-initiate
  [app-atom process-atom checkout-response]
  (let [payment-method
        (or
          (get-in @app-atom [:cart-summary :cart :options :payment])
          (:payment-method checkout-response))
        paygate?
        (or
          (contains? paygate-methods-base payment-method)
          (and
            (contains? paygate-ewallet-methods payment-method)
            (get-in @app-atom [:cart-summary :options :payments_enabled :paygate-ewallet])))]
    (cond
      paygate?
      (model/payweb3-initiate app-atom
        {:order-uuid (:order-uuid checkout-response)
         :payment-method payment-method}
        (fn [initiate-response]
          (swap! process-atom assoc :submitting? false)
          (model/clear-checkout-error app-atom)
          (if (:success initiate-response)
            (when-let [form (:redirect_form initiate-response)]
              #?(:cljs
                 (model/submit-to-paygate
                   (:pay_request_id form)
                   (:checksum form)
                   (:paygate_url initiate-response))))
            (do
              (swap! process-atom assoc :pending-checkout checkout-response)
              (toaster/open-toast app-atom :error
                "Payment initiation failed. Please try again or choose a different payment method."
                {:closetime 5000 :show-close-button true})))))

      (contains? #{"Discovery Miles" "Discovery+Miles"} payment-method)
      (model/discovery-initiate app-atom
        {:order-uuid (:order-uuid checkout-response)}
        ;; Failures are toasted (:msg) by cart-action-comms-receive
        (fn [response]
          (swap! process-atom assoc :submitting? false)
          (when (:success response)
            (when-let [redirect-url (:redirect_url response)]
              #?(:cljs (set! (.-location js/window) redirect-url))))))

      :else
      (model/fetch-order-summary app-atom
        (:order-id checkout-response)
        (:order-hash checkout-response)
        payment-method
        (fn []
          (swap! app-atom assoc-in [:order-summary :cart :options :payment] payment-method)
          (let [balance (get-in @app-atom [:order-summary :totals :balance])]
            (swap! process-atom assoc
              :step
              (if (and balance (<= balance 0))
                :order/confirmation
                :payment/capture)
              :submitting? false)))))))

(defn next-step [app-atom process-atom cart-summary-atom continue-fn e]
  (let [step-info (current-step-info process-atom)
        next-step-info (get step-def (next-step-key process-atom cart-summary-atom))
        click-fn (navigate-click-fn app-atom process-atom cart-summary-atom :next)
        checkout?
        (and
          (contains? (:properties next-step-info) :checked-out)
          (not (contains? (:properties step-info) :checked-out)))
        pending-checkout (:pending-checkout @process-atom)]
    (.preventDefault e)
    (.persist e)
    (model/clear-checkout-error app-atom)
    (swap! process-atom assoc :submitting? true)

    (when continue-fn
      (continue-fn))

    ;; Clear substep stack when navigating to a new major step
    (clear-substeps process-atom)

    (cond
      ;; Retry PayGate initiation with existing order
      (and checkout? pending-checkout)
      (try-paygate-initiate app-atom process-atom pending-checkout)

      ;; If we're already checked out, re-initiate the redirect-based methods
      ;; (PayGate card, and Discovery Miles which try-paygate-initiate also
      ;; handles) or navigate to the capture step for the in-page ones
      (contains? (:properties step-info) :checked-out)
      (let [order-summary (get @app-atom :order-summary)
            payment-method (get-in order-summary [:cart :options :payment])

            paygate?
            (or
              (contains? paygate-methods-base payment-method)
              (and
                (contains? paygate-ewallet-methods payment-method)
                (get-in order-summary [:options :payments_enabled :paygate-ewallet])))

            redirect-method?
            (or paygate? (contains? #{"Discovery Miles" "Discovery+Miles"} payment-method))]
        (if redirect-method?
          (try-paygate-initiate app-atom process-atom
            {:order-uuid (str (get-in order-summary [:order :uuid]))
             :order-id (get-in order-summary [:order :id])
             :order-hash (get-in order-summary [:order :h])
             :payment-method payment-method})
          (do
            (swap! process-atom assoc :submitting? false)
            ;; Carry the order and method in the URL (as the PHP action=pay
            ;; redirects do) so a reload of the capture step can re-load the
            ;; order instead of sitting on "Loading…"
            (let [{:keys [on-click]}
                  (urlstate/href-props app-atom "order" "ShowCart"
                    {:step "payment/capture"
                     :orderid (get-in order-summary [:order :id])
                     :h (get-in order-summary [:order :h])
                     :method payment-method})]
              (when on-click
                (on-click e))))))

;; If we're not checked out, update the cart
      :else
      (model/update-cart-delivery-details app-atom
        (fn [response]
          (let [success? (= (:success response) true)
                after-update (:after-update step-info)]
            (when-not (and success? checkout?)
              (swap! process-atom assoc :submitting? false))

            (cond
              after-update
              (after-update app-atom process-atom cart-summary-atom (partial click-fn e))

              checkout?
              (if success?
                (model/checkout app-atom process-atom cart-summary-atom
                  (fn [checkout-response]
                    (try-paygate-initiate app-atom process-atom checkout-response)))
                (swap! process-atom assoc :submitting? false))

              (and success? click-fn) (click-fn e))))
        (when (or checkout? (= :payment/method (:id next-step-info)))
          {:step
           {:id 2
            :name (or (get-in @cart-summary-atom [:cart :delivery :method]) "delivery")}})))))

(defn back-step [app-atom process-atom cart-summary-atom e]
  (.preventDefault e)
  (.persist e)
  (model/clear-checkout-error app-atom)  ; Add this line
  (swap! process-atom assoc :submitting? false)

  (let [stack (get @process-atom :substep-stack [])
        step-info (current-step-info process-atom)
        current-substep-val (current-substep process-atom)
        terminus-substeps (:terminus-substeps step-info)
        is-terminus? (and current-substep-val (contains? terminus-substeps current-substep-val))]
    (cond
      ;; If we're on a terminus substep, redirect home
      is-terminus?
      (urlstate/redirect! "/")

      ;; If substep stack has items, pop from it
      (seq stack)
      (pop-substep process-atom)

      ;; Otherwise use normal back navigation
      :else
      (do
        (clear-substeps process-atom)
        (let [click-fn (navigate-click-fn app-atom process-atom cart-summary-atom :back)]
          (when click-fn
            (click-fn e)))))))

(defn skip-step [app-atom process-atom cart-summary-atom skip-fn e]
  (let [step-info (current-step-info process-atom)
        skip-target-info (get step-def (skip-step-key process-atom cart-summary-atom))
        click-fn (navigate-click-fn app-atom process-atom cart-summary-atom :skip)
        checkout?
        (and
          (contains? (:properties skip-target-info) :checked-out)
          (not (contains? (:properties step-info) :checked-out)))]
    (.preventDefault e)
    (.persist e)
    (model/clear-checkout-error app-atom)
    (when skip-fn
      (skip-fn))
    (if checkout?
      (do
        (swap! process-atom assoc :submitting? true)
        (model/update-cart-delivery-details app-atom
          (fn [response]
            (if (= (:success response) true)
              (model/checkout app-atom process-atom cart-summary-atom
                (fn [checkout-response]
                  (try-paygate-initiate app-atom process-atom checkout-response)))
              (swap! process-atom assoc :submitting? false)))
          ;; skip lands on a checked-out step = the place-order moment
          {:step
           {:id 2
            :name (or (get-in @cart-summary-atom [:cart :delivery :method]) "delivery")}}))
      (when click-fn
        (click-fn e)))))

(defn navigate-to-step
  "Navigate directly to a specific step by step key"
  [app-atom process-atom cart-summary-atom target-step-key]
  ;; Clear substep stack when navigating to a different major step
  (clear-substeps process-atom)
  (let [href-props
        (urlstate/href-props app-atom "order" "ShowCart"
          {:step (u/keyword-string target-step-key)})]
    (when (:on-click href-props)
      ((:on-click href-props)))))

;; Helper functions for the checkout-step components
(defn shipping-notification [cart-summary-atom]
  (let [dispatch_message (cmn/curact cart-summary-atom [:dispatch_message])]
    [:div {:class "yc-section yc-section--medium"}
     (feedback/feedback
       (keyword (str/lower-case (:level dispatch_message)))
       (:title dispatch_message)
       [:span {:dangerouslySetInnerHTML {:__html (:message dispatch_message)}}])]))

(rum/defc voucher-notices-banner < rum/reactive cmn/cursors [app-atom]
  "Banner explaining voucher payments the server voided/re-sized.
   Dismissal is per code, client-side only."
  (let [notices (model/voucher-notices app-atom)]
    (when (seq notices)
      [:div.voucher-notices
       (for [{:keys [code msg]} notices]
         (rum/with-key
           (feedback/feedback :warning msg nil
             (rum/cursor-in app-atom [:voucher-notices-dismissed code]))
           (str "voucher-notice-" code)))])))

(defn validation-fix-step
  "Map a checkout-validation :field-path to the step where the customer can fix
   it. nil means it's fixable on the current (payment) step — no link needed."
  [field-path]
  (let [[a b c] field-path]
    (cond
      (and (= a :cart) (= b :delivery)
        (contains? #{:method :collection_point_uuid :collection_store_uuid} c))
      :delivery/method

      (and (= a :cart) (= b :delivery)) :details/address
      (and (= a :cart) (= b :billing)) :details/billing
      (contains? #{:items :payments :totals} a) :cart/items
      :else nil)))

(rum/defc checkout-validation-banner < rum/reactive cmn/cursors
  [app-atom process-atom cart-summary-atom]
  "Persistent panel for validations that blocked Order & Pay, with a link to the
   step where each problem can be fixed. Server validations live on the checkout
   process state (cleared by clear-checkout-error on navigation/retry); stock
   validations are also derived locally from the summary so the customer is
   warned BEFORE attempting to pay. Server entries win on field-path collisions.
   Local derivation is skipped for the postcheckout/order variant, whose summary
   has no :variations."
  (let [server-validations (cmn/curact process-atom [:cart-validation])
        summary (rum/react cart-summary-atom)

        local-validations
        (when (and (:variations summary) (not (:order summary)))
          (model/stock-blocked-validations summary))

        server-paths (set (map :field-path server-validations))

        validations
        (concat server-validations
          (remove #(contains? server-paths (:field-path %)) local-validations))]
    (when (seq validations)
      [:div {:class "yc-section yc-section--medium"}
       (feedback/feedback :error
         (if (seq server-validations)
           "We couldn't place your order"
           "Your order can't be placed yet")
         [:ul {:style {:list-style-position "inside" :padding-left 0 :margin 0}}
          (for [{:keys [code message field-path]} validations]
            (let [target (validation-fix-step field-path)]
              [:li {:key (str code "-" (pr-str field-path))}
               message
               (when target
                 [:span " — "
                  [:a
                   {:href "#"
                    :class "text-link"
                    :on-click
                    (fn [e]
                      #?(:cljs (.preventDefault e))
                      (navigate-to-step app-atom process-atom cart-summary-atom target))}
                   (str "Fix under " (:title (get step-def target)))]])]))])])))

(defn cart-login-prompt [cart-summary-atom]
  (if (cmn/curact cart-summary-atom [:is_guest])
    [:div {:key "login-prompt" :class "flex-grid-block flex-grid-block--valign-middle flex-grid-block--ml-1-2"}
     [:aside {:class "flex-grid-content"}
      [:a {:href "/login.htm?afterloginredirect=/order.htm?action=ShowCart" :class "text-link"} "Log in"]
      " to save your details and for faster checkout."]]
    nil))

(defn main-cart-content-wrapper [app-atom cart-summary-atom content]
  [:div {:key "main-cart-content" :class "flex-grid-content"}
   content])

(defn delivery-fee-label
  "Consistent delivery-fee label for a value in cents: 'Free' when zero/blank,
   else 'R{amount}'. Shared by the delivery options and the order summary so the
   free/paid wording can't drift between them."
  [cents]
  (if (and cents (pos? cents))
    (str "R" (u/currency-format cents))
    "Free"))

(defn order-calculations [app-atom cart-summary-atom]
  (let [{:keys [user totals cart] :as cart-summary} (rum/react cart-summary-atom)
        {:keys [item_total balance delivery]} totals
        {:keys [fee]} delivery
        delivery-method (get-in cart [:delivery :method])

        ;; Check for staff discount
        staff-payments (model/payment-type-list cart-summary "Staff")
        trade-payments (model/payment-type-list cart-summary "Trade")

        discount-applied-total
        (reduce (fnil + 0 0) 0
          (map :amount (concat staff-payments trade-payments)))

        ;; Registry Purchase is prepaid gift credit, not a discount — own line
        registry-credit-total
        (reduce (fnil + 0 0) 0
          (map :amount (model/payment-type-list cart-summary "Registry Purchase")))

        ;; Check for voucher payments
        voucher-payments (model/payment-type-list cart-summary "Voucher")
        voucher-total
        (reduce (fnil + 0 0) 0 (map :amount voucher-payments))

        ;; Real tender only (aka real payments)
        tender-total
        (->>
          (:payments cart-summary)
          (remove #(contains? #{"Staff" "Trade" "Registry Purchase" "Voucher"} (:pmttype %)))
          (map :amount)
          (reduce (fnil + 0 0) 0))

        ;; Check if delivery is free
        free-delivery? (zero? fee)

        delivery-label
        (case delivery-method
          ("collection" "collect") "Store collection:"
          "postnet" "PostNet collection:"
          "Delivery:")]

    [:ul {:key "order-calc" :class "order-calc"}
     ;; Staff discount status (only show if user has discount level)
     (when (:discount_level user)
       [:li {:class "order-calc__item"}
        [:span {:class "order-calc__item-name"} "Staff discount applied:"]
        [:span
         {:class
          (u/glue " "
            "order-calc__item-value"
            (if (empty? staff-payments)
              "u-text-red"
              "u-text-mint-green"))}

         (if (empty? staff-payments) "No" "Yes")]])

     ;; Items total
     [:li {:class "order-calc__item"}
      [:span {:class "order-calc__item-name"} "Items total:"]
      [:span {:class "order-calc__item-value"}
       "R" (u/currency-format (or item_total 0))]]

     ;; Vouchers redeemed (only show if there are vouchers)
     (when (pos? voucher-total)
       [:li {:class "order-calc__item"}
        [:span {:class "order-calc__item-name"} "Vouchers redeemed:"]
        [:span {:class "order-calc__item-value u-text-red"}
         "-R" (u/currency-format voucher-total)]])

     ;; Discounts applied (only show if there are discounts)
     (when (pos? discount-applied-total)
       [:li {:class "order-calc__item"}
        [:span {:class "order-calc__item-name"} "Discounts applied:"]
        [:span {:class "order-calc__item-value u-text-red"}
         "-R" (u/currency-format discount-applied-total)]])

     ;; Registry credit (only show if there is credit applied)
     (when (pos? registry-credit-total)
       [:li {:class "order-calc__item"}
        [:span {:class "order-calc__item-name"} "Registry credit:"]
        [:span {:class "order-calc__item-value u-text-red"}
         "-R" (u/currency-format registry-credit-total)]])

     ;; Delivery
     [:li {:class "order-calc__item"}
      [:span {:class "order-calc__item-name"} delivery-label]
      [:span {:class "order-calc__item-value"}
       (cond
         free-delivery? [:span {:class "u-text-red"} (delivery-fee-label fee)]
         (u/presence delivery-method) (delivery-fee-label fee)
         :else "(Calculated at next step)")]]

     ;; Payments applied — real tender only (card/EFT/cash/eBucks etc.)
     (when (pos? tender-total)
       [:li {:class "order-calc__item"}
        [:span {:class "order-calc__item-name"} "Payments applied:"]
        [:span {:class "order-calc__item-value u-text-red"}
         "-R" (u/currency-format tender-total)]])

     ;; Owing (conditional text based on free delivery)
     [:li {:class "order-calc__item"}
      [:span {:class "order-calc__item-name"}
       (if (or free-delivery? (u/presence delivery-method))
         "Owing:"
         "Owing (excl. delivery):")]
      [:span {:class "order-calc__item-value order-calc__item-value--total"}
       "R"
       (u/currency-format
         (max 0
           (if (u/presence delivery-method)
             (or balance 0)
             (- (or balance 0) (or fee 0)))))]]]))

;; Additional helper functions needed for checkout-step
(rum/defc placeholder-include [file-path params]
  [:span]
  #_[:div {:style {:border "1px dashed #aaa" :padding "10px" :margin "5px 0" :background-color "#f9f9f9"}
           :tooltip "Hello"}
     (buttons/button-icon {:icon :error :icon-display :inline})
     [:span {:style {:font-size "8px"}} file-path]
     #_[:span {:style {:font-size "6px"}} (pr-str params)]
     #_[:p "Placeholder for include: " [:strong file-path]]
     #_(when (seq params)
         [:pre (pr-str params)])])

(rum/defc checkout-secure-badge []
  [:div {:class "checkout-secure-badge"}
   [:i {:class "icon icon--inline icon--lock icon--is-dark"}]
   [:span {:class "checkout-secure-badge__text"} "Safe and secure checkout."]])

(rum/defc checkout-later [app-atom & [{:keys [disabled?]}]]
  [:div {:class "checkout-later"}
   (buttons/button-web "Save cart for later?"
     {:on-click
      #(do
         (.preventDefault %)
         (grid/open-modal app-atom :cart/save-for-later {}))
      :disabled disabled?
      :class "checkout-later__button"
      :style :secondary
      :flat-style :raised})])

;; Extracted CTA components
(rum/defc checkout-balance-display < rum/reactive cmn/cursors [cart-atom step-info]
  "Display the owing amount and delivery messages"
  (let [cart-summary (rum/react cart-atom)
        {:keys [balance delivery]} (:totals cart-summary)
        {:keys [fee]} delivery
        delivery-method (get-in cart-summary [:cart :delivery :method])

        actual-balance
        (max 0
          (if (u/presence delivery-method)
            balance
            (- balance fee)))
        free-delivery? (zero? fee)
        delivery-needed?  (model/has-physical-items? cart-summary)
        current-step (:id step-info)]
    [:div {:class "flex-grid-content"}
     [:p {:class "yc-paragraph yc-paragraph--large yc-paragraph--flush u-text-center"}
      [:b "Owing: R" (u/currency-format actual-balance)]
      (when (:order cart-summary)
        [:small {:class "u-block u-text-center u-text-12"} (str "Order no. " (get-in cart-summary [:order :number]))])
      (when (= current-step :payment/method)
        (let [selected-payment (get-in cart-summary [:cart :options :payment])
              payment-title
              (case selected-payment
                "Card" "Pay by card"
                "Amex" "Pay with Amex card"
                "Mobicred" "Pay with Mobicred"
                "Payflex" "Pay with Payflex"
                "Transfer" "Pay by EFT (Bank Transfer)"
                "eBucks" "Pay using eBucks"
                "Discovery Miles" "Pay using Discovery Miles"
                "Zapper" "Pay using Zapper"
                "SnapScan" "Pay using SnapScan"
                "InStore" "Pay in store"
                nil)]
          (when payment-title
            [:small {:class "u-block u-text-center u-text-12"} payment-title])))]

     (cond
       (not delivery-needed?)
       [:small {:class "u-block u-text-center u-text-12"} "Digital delivery"]

       (and
         (= current-step :cart/items)
         (not (:order cart-summary))
         (or
           (seq (model/stock-blocked-validations cart-summary))
           (seq (model/lines-with-checkout-issues cart-summary))))
       [:small {:class "u-block u-text-center u-text-12 u-text-red"}
        "Some items in your cart need updating before you can check out."]

       (and (= current-step :cart/items) (not (u/presence delivery-method)))
       [:small {:class "u-block u-text-center u-text-12"} "Delivery cost at next step"]

       (and (= current-step :delivery/method)
         (model/missing-collection-choice? cart-summary))
       [:small {:class "u-block u-text-center u-text-12 u-text-red"}
        (if (= delivery-method "collect")
          "Select a store to collect from to continue."
          "Select a PostNet store to continue.")]

       (and free-delivery? (contains? #{:delivery/method :details/address :details/billing :payment/capture} current-step))
       [:small {:class "u-block u-text-center u-text-12"}
        (case delivery-method
          ("collection" "collect") "Collect from store"
          "postnet" "Collect from PostNet"
          "You get free delivery")])

     #_(when (and (contains? #{:details/address :details/billing} current-step) (not free-delivery?))
         [:small {:class "u-block u-text-center u-text-12"} "" #_"Final amount calculated at payment"])]))

(rum/defc checkout-continue-button < rum/reactive cmn/cursors
  [app-atom process-atom cart-atom
   {:keys [disabled? continue-fn] :as opts}]
  "Main continue/checkout button"
  (let [{:keys [user items] :as cart-summary} (rum/react cart-atom)
        step-info (current-step-info process-atom)
        next-step-info (get step-def (next-step-key process-atom cart-atom))

        unavailable?
        (or
          disabled?
          (cmn/curact process-atom [:submitting?])
          (:disabled (:applystaffdiscount cart-summary))
          (and (not (:order cart-summary))
            (not (model/has-registry-purchase-items? cart-summary))
            (model/has-discontinued-items? cart-summary)))

        complete-order?
        (and
          (contains? (:properties next-step-info) :checked-out)
          (not (contains? (:properties step-info) :checked-out)))

        ;; Get button text from step definition
        button-text
        (or
          (:button-text step-info)
          (when complete-order?
            "Complete Order")
          "Continue")

        button-style
        (or
          (:button-style step-info)
          (when complete-order?
            :cta)
          :primary)

        form-node-atom (cmn/cursor-in process-atom [:form-node])
        validate
        (if (rum/react form-node-atom)
          (forms/form-validate form-node-atom
            #(next-step app-atom process-atom cart-atom continue-fn %))
          (fn [e]
            (next-step app-atom process-atom cart-atom continue-fn e)))]

    [:div {:class "flex-grid-content"}
     (if unavailable?
       (buttons/button-web button-text
         {:disabled true
          :class "checkout-cta__button"
          :full true
          :flat-style :raised})
       (buttons/button-web button-text
         {:on-click
          (fn [e]
            (.preventDefault e)
            (if (:continue-fn step-info)
              ((:continue-fn step-info) app-atom)
              (validate e)))

          :class "checkout-cta__button"
          :full true
          :flat-style :raised
          :style button-style
          :icon :arrow-right
          :icon-placement :right}))]))

(rum/defc checkout-skip-button < rum/reactive cmn/cursors [app-atom process-atom cart-summary-atom {:keys [skip-fn skip-disabled?] :as opts}]
  "Skip button for optional steps"
  [:div {:class "flex-grid-content"}
   (buttons/button-web "Skip this step"
     {:on-click #(skip-step app-atom process-atom cart-summary-atom skip-fn %)
      :disabled skip-disabled?
      :class "checkout-cta__button checkout-cta__button--secondary"
      :full true
      :style :secondary
      :flat-style :raised})])

(rum/defc checkout-cta-sidebar < rum/reactive cmn/cursors
  [app-atom process-atom cart-summary-atom
   {:keys [disabled? continue-fn skip-fn notice] :as opts}]
  "Sticky checkout button with total - updated to match HTML master file structure"
  (let [cart-busy?
        (or
          (seq (rum/react model/qty-pending))
          (seq (rum/react model/removing-groups)))
        opts (if cart-busy? (assoc opts :disabled? true) opts)
        step-info (current-step-info process-atom)]
    [:div {:class "flex-grid-content"}
     [:div {:class "checkout-cta-wrapper"}

      ;; Step-blocking notices (e.g. checkout validation errors) directly above
      ;; the button they blocked
      notice

      ;; Main checkout CTA with balance display and buttons
      [:div {:class "checkout-cta"}
       [:div {:class "flex-grid-group flex-grid-group--flush-force flex-grid-group--valign-middle"}

        (let [skippable? (contains? (:properties step-info) :skippable)
              skip-target-info (when skippable? (get step-def (skip-step-key process-atom cart-summary-atom)))
              skip-leads-to-checkout?
              (and
                skip-target-info
                (contains? (:properties skip-target-info) :checked-out)
                (not (contains? (:properties step-info) :checked-out)))]
          (if (and skippable? (not skip-leads-to-checkout?))
            [:<>
             [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--t-order-02 flex-grid-block--l-full flex-grid-block--l-order-01"}
              (checkout-continue-button app-atom process-atom cart-summary-atom opts)]

             [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--t-order-01 flex-grid-block--l-full flex-grid-block--l-order-02"}
              (checkout-skip-button app-atom process-atom cart-summary-atom opts)]]

            [:<>
             (when-not (contains? (:properties step-info) :hide-amount)
               [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--l-full"}
                (checkout-balance-display cart-summary-atom step-info)])
             [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--l-full"}
              (checkout-continue-button app-atom process-atom cart-summary-atom opts)]]))]]

      ;; Security badge
      (checkout-secure-badge)

      ;; Save cart for later (hidden when cart has unavailable items)
      (when
        (and
          (contains? (:properties step-info) :save-cart)
          (not= "warn" (:level (cmn/curact cart-summary-atom [:dispatch_message]))))
        (checkout-later app-atom {:disabled? cart-busy?}))]]))

(rum/defc checkout-cta-sidebar-simple < rum/reactive
  [primary-button secondary-button order-number cart-atom
   & [{:keys [total-key hide-totals-mobile?]}]]
  "Simplified checkout CTA sidebar - pass pre-built buttons directly"
  [:div {:class "flex-grid-content"}
    ;; Checkout CTA wrapper
   [:div {:class "checkout-cta-wrapper js-checkout-cta-wrapper"}
    [:div {:class "checkout-cta"}
     [:div {:class "flex-grid-group flex-grid-group--flush-force flex-grid-group--valign-middle"}
      (when primary-button
        [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--t-order-02 flex-grid-block--l-full flex-grid-block--l-order-01"}
         [:div {:class "flex-grid-content"}
          primary-button]])

      (when secondary-button
        [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--t-order-01 flex-grid-block--l-full flex-grid-block--l-order-02"}
         [:div {:class "flex-grid-content"}
          secondary-button]])

      (when order-number
        [:div {:class (str "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--l-full"
                        (when hide-totals-mobile? " u-hide-block u-display-block--l"))}
         [:div {:class "flex-grid-content"}
          [:p {:class "yc-paragraph yc-paragraph--large yc-paragraph--flush u-text-center"}
           [:b
            (case total-key
              :balance
              (str "Owing: R" (u/currency-format (cmn/curact cart-atom [:totals :balance])))
              (str "Total: R" (u/currency-format (cmn/curact cart-atom [:totals :total]))))]
           [:small {:class "u-block u-text-center u-text-12"} (str "Order no. " order-number)]]]])]]

    [:div {:class "checkout-secure-badge"}
     [:i {:class "icon icon--inline icon--lock icon--is-dark"}]
     [:span {:class "checkout-secure-badge__text"} "Safe and secure checkout."]]]])

(defmulti checkout-step
  (fn [app-atom process-atom cart-summary-atom]
    (or (cmn/curact process-atom [:step]) :cart/items)))

(defmethod checkout-step :default
  [app-atom process-atom cart-summary-atom]
  (let [current-step (cmn/curact process-atom [:step])]
    [:div (pr-str current-step)]))

;; Helper functions for the checkout-step components

(rum/defc skip-button
  "Visually hidden skip button for accessibility (per HTML comment)"
  [target-id]

  (buttons/button-web "Skip to main content"
    {:href (or target-id "#main-content")
     :class "skip-button"
     :style :secondary
     :flat-style :raised}))

(rum/defc placeholder-insert [component-name params]
  [:span]
  #_[:div {:style {:border "1px dashed #ccc" :padding "10px" :margin "5px 0"}}
     (buttons/button-icon {:icon :error :icon-display :inline})
     [:span {:style {:font-size "8px"}} component-name]
     #_[:span {:style {:font-size "6px"}} (pr-str params)]
     #_[:p "Placeholder for " [:strong component-name]]
     #_(when (seq params)
         [:pre (pr-str params)])]) ;; Use pr-str for params

(defn product-search [params]
  (placeholder-insert "productSearch" params))

(rum/defc checkout-layout [process-atom form? content cta]
  [:main {:id "main-content" :class "yc-section"}

   [(if form? :form :div)
    {:ref
     (fn [node]
       (swap! process-atom assoc :form-node
         (when form? node)))
     :class "flex-grid-group"}

    [:div {:class "flex-grid-block flex-grid-block--ml-3-4 flex-grid-block--l-2-3"}
     [:div {:class "flex-grid-content"}

      content]]

          ;; Right column: CTA sidebar
    [:div {:class "flex-grid-block flex-grid-block--t-full flex-grid-block--l-1-3"}
     cta]]])

;; Cart item expiry countdown component
(defn parse-expires-at
  "Parse the expires_at value to a timestamp (milliseconds)"
  [expires-at]
  (when expires-at
    (if (number? expires-at)
      expires-at
      (date/millis expires-at))))

(defn calculate-time-remaining
  "Calculate time remaining in seconds from expires_at timestamp"
  [expires-at-ms]
  (let [now-ms
        #?(:cljs (.getTime (js/Date.))
           :clj (System/currentTimeMillis))
        remaining-ms (- expires-at-ms now-ms)]
    (max 0 (Math/floor (/ remaining-ms 1000)))))

(defn format-countdown
  "Format seconds into a human readable countdown string"
  [seconds]
  (let [minutes (Math/floor (/ seconds 60))
        secs (mod seconds 60)]
    (cond
      (< seconds 60) :removing-soon
      :else (str minutes " min " secs " sec"))))

(rum/defcs expiry-countdown < rum/reactive
  {:did-mount
   (fn [state]
     #?(:cljs
        (let [interval-id
              (js/setInterval
                (fn []
                  (when-let [local-atom (:local-atom state)]
                    (swap! local-atom update :tick (fnil inc 0))))
                1000)]
          (assoc state ::interval-id interval-id))))
   :will-unmount
   (fn [state]
     #?(:cljs
        (when-let [interval-id (::interval-id state)]
          (js/clearInterval interval-id)))
     state)
   :init
   (fn [state _]
     (assoc state :local-atom (atom {:tick 0})))}
  [state expires-at & [{:keys [mini?]}]]
  (let [_ (rum/react (:local-atom state)) ;; Force re-render on tick
        expires-at-ms (parse-expires-at expires-at)
        remaining-secs (when expires-at-ms (calculate-time-remaining expires-at-ms))
        countdown-text (when-not (nil? remaining-secs) (format-countdown remaining-secs))]

    ;; Once the countdown is complete, clear the interval so we stop updating the component.
    #?(:cljs
       (when (or (nil? countdown-text) (= countdown-text :removing-soon))
         (js/clearInterval (::interval-id state))))

    (cond
      ;; No expiry or expired
      (nil? countdown-text)
      nil

      ;; Less than 1 minute - show removal warning
      (= countdown-text :removing-soon)
      (if mini?
        [:span {:class "text-status text-status--important"}
         "This item will be removed soon."]
        [:p {:class "cart-item__stock-message"}
         [:span {:class "text-status text-status--important"}
          "This item will be removed soon."]])

      ;; Show countdown
      :else
      (if mini?
        [:span {:class "text-status text-status--important"}
         (str "Time left: " countdown-text " to check out this item.")]
        [:p {:class "cart-item__stock-message"}
         [:span {:class "text-status text-status--important"}
          (str "Time left: " countdown-text " to check out this item.")]]))))



